import React from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Bot, Zap, Compass, ShieldCheck, Clock } from "lucide-react";
import { SemChat, SemHeader } from "@/components/SemChat";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const Sem = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const initialQ = params.get("q") || "";

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="relative bg-[#FDFCFB] flex-1">
        {/* Ambient blobs */}
        <div className="absolute top-32 -left-40 w-[480px] h-[480px] rounded-full bg-[#F5A623]/10 blur-3xl pointer-events-none bc-breath" />
        <div className="absolute bottom-20 -right-40 w-[460px] h-[460px] rounded-full bg-[#0A1128]/8 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-16 relative">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14">
            {/* LEFT — Chat surface */}
            <div className="bc-fade-up">
              <div className="mb-6">
                <span className="pill-amber">
                  <Sparkles size={11} className="mr-1" /> {t("sem.badge")}
                </span>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-[56px] font-medium text-[#0A1128] tracking-tight mt-4 leading-[1.05]">
                  {t("sem.page_title_1")}<span className="text-[#F5A623]">SEM</span>{t("sem.page_title_2")}
                </h1>
                <p className="text-[#0A1128]/65 mt-4 md:text-lg leading-relaxed max-w-xl">
                  {t("sem.page_subtitle")}
                </p>
              </div>

              <div className="bg-white border border-[#EAE6DF] rounded-md overflow-hidden shadow-[0_8px_32px_rgba(10,17,40,0.06)]">
                <SemHeader />
                <SemChat variant="page" autoSubmit={initialQ} />
              </div>
            </div>

            {/* RIGHT — Editorial sidebar */}
            <aside className="space-y-4 lg:pt-32 bc-fade-up bc-fade-up-d2">
              {/* Why SEM */}
              <div className="surface-cream p-6">
                <span className="eyebrow">{t("sem.why_eyebrow")}</span>
                <h3 className="font-heading text-2xl text-[#0A1128] mt-3 leading-tight tracking-tight">
                  {t("sem.why_title")}
                </h3>
                <p className="text-[13px] text-[#0A1128]/60 mt-3 leading-relaxed">
                  {t("sem.why_body")}
                </p>
              </div>

              {/* Features */}
              {[
                { icon: Bot, t: t("sem.feat1_t"), d: t("sem.feat1_d") },
                { icon: Zap, t: t("sem.feat2_t"), d: t("sem.feat2_d") },
                { icon: Compass, t: t("sem.feat3_t"), d: t("sem.feat3_d") },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.t} className="bg-white border border-[#EAE6DF] rounded-md p-5 flex items-start gap-4 hover:border-[#0A1128]/20 transition-colors">
                    <div className="w-10 h-10 rounded-md bg-[#0A1128] text-[#F5A623] flex items-center justify-center flex-shrink-0">
                      <Icon size={18} strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-medium text-[#0A1128] tracking-tight">{f.t}</h3>
                      <p className="text-[12px] text-[#0A1128]/60 mt-1 leading-relaxed">{f.d}</p>
                    </div>
                  </div>
                );
              })}

              {/* Trust strip */}
              <div className="bg-[#0A1128] text-white rounded-md p-5">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <ShieldCheck size={16} className="text-[#F5A623] flex-shrink-0" />
                    <span className="text-white/90">{t("home.hero.trust_refund")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock size={16} className="text-[#F5A623] flex-shrink-0" />
                    <span className="text-white/90">{t("home.hero.minute")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <ShieldCheck size={16} className="text-[#F5A623] flex-shrink-0" />
                    <span className="text-white/90">{t("home.hero.trust_verified")}</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Sem;
